import { useState, useEffect, useRef } from "react";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { useInteractionLogger } from "@/hooks/useInteractionLogger";
import LearnerAnnotations from "@/components/LearnerAnnotations";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CheckCircle, XCircle, HelpCircle, ArrowLeft, Star, ImageIcon, ChevronDown, Loader2, Trophy, RefreshCw } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import FurtherReading from "@/components/FurtherReading";
import MindMapLessonView, { type MindMapLesson } from "@/components/MindMapLessonView";
import SimulationLessonView, { type SimulationConfig } from "@/components/SimulationLessonView";
import LessonTutorChat from "@/components/LessonTutorChat";

interface Slide {
  title: string;
  bullets?: string[];
  paragraph?: string;
  notes?: string;
  imageUrl?: string;
}

interface ExamQuestion {
  id: string;
  question_type: string;
  prompt: string;
  options: string[] | null;
  explanation: string;
  points: number;
}

interface ExamAttemptFeedback {
  questionId: string;
  questionType?: string;
  prompt: string;
  userAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  creditPercent?: number;
  aiScorePercent?: number | null;
  aiRationale?: string | null;
  explanation: string;
  pointsEarned: number;
  pointsPossible: number;
}

function examCreditPercent(f: ExamAttemptFeedback): number {
  if (typeof f.creditPercent === "number" && Number.isFinite(f.creditPercent)) return f.creditPercent;
  const p = f.pointsPossible;
  if (p > 0) return Math.round((f.pointsEarned / p) * 100);
  return 0;
}

function creditStatusLabel(credit: number): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  if (credit >= 100) return { label: "Full credit", variant: "default" };
  if (credit >= 70) return { label: "Strong partial", variant: "secondary" };
  if (credit >= 40) return { label: "Partial", variant: "outline" };
  if (credit > 0) return { label: "Limited", variant: "outline" };
  return { label: "No credit", variant: "destructive" };
}

interface LearnerContentViewProps {
  skillNodeId: string;
  courseId?: string;
  nodeTitle: string;
  isKeyNode?: boolean;
  onProgressUpdate?: () => void;
  onBack?: () => void;
  notesPanelOpen?: boolean;
  onNotesPanelOpenChange?: (open: boolean) => void;
  notesPanelContainerId?: string;
}

interface MasteryQuestion {
  id: string;
  type: "mcq" | "true_false";
  difficulty: "easy" | "medium" | "hard";
  prompt: string;
  options?: string[];
  correctIndex?: number;
  correct?: boolean;
  explanation: string;
}

interface MasteryQuestionFeedback {
  id: string;
  userCorrect: boolean;
  explanation: string;
}

// Module-level cache for content (instant display on back/forward, refresh in background)
const contentCache = new Map<
  string,
  { lessonContent: string | null; slides: Slide[]; nodeDetail: any; examData: any; courseId?: string }
>();

export default function LearnerContentView({
  skillNodeId,
  courseId,
  nodeTitle,
  isKeyNode = false,
  onProgressUpdate,
  onBack,
  notesPanelOpen = false,
  onNotesPanelOpenChange,
  notesPanelContainerId,
}: LearnerContentViewProps) {
  const [loading, setLoading] = useState(true);
  const [lessonContent, setLessonContent] = useState<string | null>(null);
  const [mindMapLesson, setMindMapLesson] = useState<MindMapLesson | null>(null);
  const [simulationLesson, setSimulationLesson] = useState<SimulationConfig | null>(null);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [nodeDetail, setNodeDetail] = useState<any>(null);
  const [examData, setExamData] = useState<any>(null);
  const [senseCheckAnswers, setSenseCheckAnswers] = useState<Record<string, string>>({});
  const [senseCheckFeedback, setSenseCheckFeedback] = useState<Record<string, { correct: boolean; show: boolean; explanation?: string }>>({});
  const [senseCheckLoading, setSenseCheckLoading] = useState<Record<string, boolean>>({});
  const [takingExam, setTakingExam] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [examResult, setExamResult] = useState<any>(null);
  const [examAnswers, setExamAnswers] = useState<Record<string, string>>({});
  const [completingLesson, setCompletingLesson] = useState(false);
  const [lessonMarkedComplete, setLessonMarkedComplete] = useState(false);
  const [masteryQuestions, setMasteryQuestions] = useState<MasteryQuestion[] | null>(null);
  const [masteryLoading, setMasteryLoading] = useState(false);
  const [masteryAnswers, setMasteryAnswers] = useState<Record<string, string>>({});
  const [masteryResult, setMasteryResult] = useState<{ score: number; total: number; passed: boolean; feedback: MasteryQuestionFeedback[] } | null>(null);
  const annotationsContainerRef = useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();
  const { log } = useInteractionLogger(courseId, skillNodeId);

  useEffect(() => {
    const cacheKey = `${skillNodeId}:${courseId ?? "standalone"}`;
    const cached = contentCache.get(cacheKey);
    if (cached) {
      setLessonContent(cached.lessonContent);
      setSlides(cached.slides);
      setNodeDetail(cached.nodeDetail);
      setExamData(cached.examData);
      setLoading(false);
    }
    fetchAllContent();
  }, [skillNodeId, courseId]);

  const fetchAllContent = async () => {
    const cacheKey = `${skillNodeId}:${courseId ?? "standalone"}`;
    const cached = contentCache.get(cacheKey);
    if (!cached) {
      setLoading(true);
      setLessonContent(null);
      setMindMapLesson(null);
      setSimulationLesson(null);
      setSlides([]);
      setNodeDetail(null);
      setExamData(null);
    }
    // If cached, keep showing cached content while we refresh in background

    const examFn = courseId ? "get-adaptive-exam" : "get-skill-node-exam";
    const examBody = courseId ? { skillNodeId, courseId } : { skillNodeId };
    const [detailRes, contentRes, deckRes, examRes] = await Promise.all([
      invokeFunction("get-skill-node-detail", { body: { skillNodeId } }),
      supabase.from("skill_node_content").select("content, content_type").eq("skill_node_id", skillNodeId).maybeSingle(),
      supabase
        .from("generated_slide_decks")
        .select("slide_deck_json")
        .eq("skill_node_id", skillNodeId)
        .order("version", { ascending: false })
        .limit(1)
        .maybeSingle(),
      invokeFunction(examFn, { body: examBody }),
    ]);

    if (detailRes.data) setNodeDetail(detailRes.data);
    const tableContent = contentRes.data?.content?.trim();

    // Detect mind map / simulation by JSON structure
    let detectedMindMap = false;
    let detectedSimulation = false;
    if (tableContent) {
      try {
        const parsed = JSON.parse(tableContent);
        if (parsed.centralTopic && Array.isArray(parsed.nodes)) {
          setMindMapLesson(parsed as MindMapLesson);
          detectedMindMap = true;
        } else if (parsed.type === "simulation" && typeof parsed.url === "string") {
          setSimulationLesson(parsed as SimulationConfig);
          detectedSimulation = true;
        }
      } catch { /* not JSON — fall through to regular text */ }
    }

    if (!detectedMindMap && !detectedSimulation && tableContent && tableContent.length > 100) {
      setLessonContent(tableContent);
    } else if (detailRes.data?.content?.content?.trim() && (detailRes.data.content.content as string).trim().length > 100) {
      setLessonContent((detailRes.data.content.content as string).trim());
    }
    if (deckRes.data?.slide_deck_json) {
      const deck = deckRes.data.slide_deck_json as { slides?: Slide[] };
      setSlides(deck.slides || []);
    }
    if (examRes.data?.questions?.length > 0) setExamData(examRes.data);

    const gotContent = !!(tableContent && tableContent.length > 100) || !!(detailRes.data?.content?.content?.trim() && (detailRes.data.content.content as string).trim().length > 100);
    if (!gotContent) {
      console.warn("[LearnerContentView] No content for node:", skillNodeId, {
        "get-skill-node-detail": detailRes.error ? { error: detailRes.error, status: (detailRes.error as { status?: number }).status } : { ok: !!detailRes.data, hasContentRow: !!detailRes.data?.content },
        "skill_node_content (table)": contentRes.error ? { error: contentRes.error.message } : { ok: !!contentRes.data, contentLength: contentRes.data?.content?.length ?? 0 },
        "generated_slide_decks (table)": deckRes.error ? { error: deckRes.error.message } : { ok: !!deckRes.data, slidesCount: (deckRes.data?.slide_deck_json as { slides?: unknown[] })?.slides?.length ?? 0 },
        [examFn]: examRes.error ? { error: examRes.error, status: (examRes.error as { status?: number }).status } : { ok: !!examRes.data },
      });
    }

    // Update cache for instant display on revisit
    const finalLessonContent =
      tableContent && tableContent.length > 100
        ? tableContent
        : detailRes.data?.content?.content?.trim() && (detailRes.data.content.content as string).trim().length > 100
          ? (detailRes.data.content.content as string).trim()
          : null;
    const finalSlides = (deckRes.data?.slide_deck_json as { slides?: Slide[] })?.slides || [];
    contentCache.set(cacheKey, {
      lessonContent: finalLessonContent,
      slides: finalSlides,
      nodeDetail: detailRes.data ?? null,
      examData: examRes.data?.questions?.length ? examRes.data : null,
      courseId,
    });

    setLoading(false);
  };

  const questions: ExamQuestion[] = examData?.questions || [];
  const senseCheckQuestions = questions.slice(0, Math.min(3, Math.ceil(questions.length / 2)));
  const finalExamQuestions = questions;

  const handleSenseCheckSubmit = async (questionId: string) => {
    const answer = senseCheckAnswers[questionId];
    if (!answer) return;
    setSenseCheckLoading((p) => ({ ...p, [questionId]: true }));
    const { data, error } = await invokeFunction("check-sense-answer", {
      body: { questionId, answerValue: answer },
    });
    setSenseCheckLoading((p) => ({ ...p, [questionId]: false }));
    if (error) {
      toast({ variant: "destructive", title: "Could not check answer" });
      return;
    }
    const correct = data?.correct ?? false;
    setSenseCheckFeedback((prev) => ({ ...prev, [questionId]: { correct, show: true, explanation: data?.explanation } }));
    if (courseId) log("sense_check_answered", { correct });
  };

  const handleCompleteLessonWithoutExam = async () => {
    if (!courseId) return;
    setCompletingLesson(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        toast({ variant: "destructive", title: "Sign in required" });
        return;
      }
      const { error: updateError } = await supabase
        .from("user_skill_node_progress")
        .update({ status: "completed", last_changed_at: new Date().toISOString() })
        .eq("user_id", session.user.id)
        .eq("skill_node_id", skillNodeId);
      if (updateError) throw updateError;
      const { error: rpcError } = await supabase.rpc("update_node_availability", {
        p_user_id: session.user.id,
        p_course_id: courseId,
      });
      if (rpcError) console.warn("update_node_availability:", rpcError);
      setLessonMarkedComplete(true);
      toast({ title: "Lesson complete!", description: "Next lessons unlocked." });
      onProgressUpdate?.();
    } catch (e: any) {
      toast({ variant: "destructive", title: "Could not complete lesson", description: e?.message });
    } finally {
      setCompletingLesson(false);
    }
  };

  const generateMasteryCheck = async () => {
    if (!courseId) return;
    setMasteryLoading(true);
    setMasteryQuestions(null);
    setMasteryAnswers({});
    setMasteryResult(null);
    try {
      const { data, error } = await invokeFunction("course-assistant", {
        body: {
          messages: [],
          context: { courseId, nodeId: skillNodeId, nodeName: nodeTitle, pageType: "learner" },
          mode: "mastery_check",
        },
      });
      if (error) throw new Error((error as { message?: string }).message || "Failed to generate questions");
      if (Array.isArray(data?.questions) && data.questions.length > 0) {
        setMasteryQuestions(data.questions as MasteryQuestion[]);
      } else {
        throw new Error("No questions returned");
      }
    } catch {
      toast({ variant: "destructive", title: "Could not generate mastery check", description: "Please try again." });
    } finally {
      setMasteryLoading(false);
    }
  };

  const submitMasteryCheck = () => {
    if (!masteryQuestions) return;
    const unanswered = masteryQuestions.filter((q) => masteryAnswers[q.id] === undefined);
    if (unanswered.length > 0) {
      toast({ variant: "destructive", title: "Incomplete", description: "Please answer all questions." });
      return;
    }
    let score = 0;
    const feedback: MasteryQuestionFeedback[] = masteryQuestions.map((q) => {
      let correct = false;
      if (q.type === "mcq" && q.options && typeof q.correctIndex === "number") {
        correct = masteryAnswers[q.id] === String(q.correctIndex);
      } else if (q.type === "true_false") {
        correct = masteryAnswers[q.id] === String(q.correct);
      }
      if (correct) score++;
      return { id: q.id, userCorrect: correct, explanation: q.explanation };
    });
    const passed = score >= Math.ceil(masteryQuestions.length * 0.6);
    setMasteryResult({ score, total: masteryQuestions.length, passed, feedback });
    log("exam_submitted", { score: Math.round((score / masteryQuestions.length) * 100), passed, type: "mastery_check" });
    if (passed) {
      toast({ title: "Nice work!", description: `You scored ${score}/${masteryQuestions.length}. Lesson complete!` });
    }
  };

  const handleStartExam = () => {
    setTakingExam(true);
    setExamResult(null);
    setExamAnswers({});
  };

  const handleSubmitExam = async () => {
    if (!examData) return;
    const unanswered = finalExamQuestions.filter((q) => !examAnswers[q.id]);
    if (unanswered.length > 0) {
      toast({
        variant: "destructive",
        title: "Incomplete",
        description: `Please answer all ${finalExamQuestions.length} questions.`,
      });
      return;
    }

    setSubmitting(true);
    const { data, error } = await invokeFunction("attempt-exam", {
      body: {
        examId: examData.exam.id,
        answers: Object.entries(examAnswers).map(([questionId, answerValue]) => ({ questionId, answerValue })),
      },
    });

    if (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } else {
      setExamResult(data);
      setTakingExam(false);
      if (courseId) log("exam_submitted", { score: data.score, passed: data.passed });
      if (data.passed) {
        toast({ title: "Congratulations!", description: `You passed with ${data.score}%!` });
        onProgressUpdate?.();
      } else {
        toast({ variant: "destructive", title: "Not quite", description: `Scored ${data.score}%. Try again!` });
      }
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading lesson...</div>
      </div>
    );
  }

  const hasContent = lessonContent || mindMapLesson || simulationLesson || slides.length > 0;

  // Build interleaved blocks: sprinkle text sections, slides, and sense checks throughout
  const markdownComponents = {
    h1: ({ children }: any) => <h1 className="scroll-mt-24">{children}</h1>,
    h2: ({ children }: any) => <h2 className="scroll-mt-24">{children}</h2>,
    h3: ({ children }: any) => <h3 className="scroll-mt-20">{children}</h3>,
    img: ({ src, alt }: any) => (
      <figure className="my-8">
        <img src={src || ""} alt={alt || ""} className="rounded-xl shadow-lg w-full" />
        {alt && <figcaption className="text-sm text-muted-foreground mt-2 text-center">{alt}</figcaption>}
      </figure>
    ),
    table: ({ children }: any) => (
      <div className="my-6 overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[400px]">{children}</table>
      </div>
    ),
    thead: ({ children }: any) => <thead className="bg-muted">{children}</thead>,
    th: ({ children }: any) => <th className="px-4 py-3 text-left text-sm font-semibold border-b-2 border-border">{children}</th>,
    td: ({ children }: any) => <td className="px-4 py-3 text-sm border-b border-border align-top">{children}</td>,
    tr: ({ children }: any) => <tr className="border-b border-border last:border-b-0 hover:bg-muted/50 transition-colors">{children}</tr>,
  };

  type ContentBlock = { type: "text"; content: string } | { type: "slide"; slide: Slide; idx: number } | { type: "sense_check"; question: ExamQuestion };
  const contentBlocks: ContentBlock[] = [];

  if (lessonContent && slides.length > 0) {
    // Interleave essay sections with slides and sense checks
    let sections = lessonContent.split(/\n(?=## |### )/).filter(Boolean);
    if (sections.length === 0) sections = [lessonContent];
    let slideIdx = 0;
    let senseIdx = 0;
    for (let i = 0; i < sections.length; i++) {
      contentBlocks.push({ type: "text", content: sections[i].trim() });
      if (slideIdx < slides.length) contentBlocks.push({ type: "slide", slide: slides[slideIdx], idx: slideIdx++ });
      if (senseIdx < senseCheckQuestions.length) contentBlocks.push({ type: "sense_check", question: senseCheckQuestions[senseIdx++] });
    }
    while (slideIdx < slides.length) contentBlocks.push({ type: "slide", slide: slides[slideIdx], idx: slideIdx++ });
    while (senseIdx < senseCheckQuestions.length) contentBlocks.push({ type: "sense_check", question: senseCheckQuestions[senseIdx++] });
  } else if (lessonContent) {
    // Essay only: split by headings, sprinkle sense checks between sections
    let sections = lessonContent.split(/\n(?=## |### )/).filter(Boolean);
    if (sections.length === 0) sections = [lessonContent];
    let senseIdx = 0;
    for (let i = 0; i < sections.length; i++) {
      contentBlocks.push({ type: "text", content: sections[i].trim() });
      if (senseIdx < senseCheckQuestions.length) contentBlocks.push({ type: "sense_check", question: senseCheckQuestions[senseIdx++] });
    }
  } else if (slides.length > 0) {
    // Slides only: sprinkle sense check after every slide
    slides.forEach((slide, idx) => {
      contentBlocks.push({ type: "slide", slide, idx });
      if (senseCheckQuestions[idx]) contentBlocks.push({ type: "sense_check", question: senseCheckQuestions[idx] });
    });
    if (senseCheckQuestions.length > slides.length) {
      senseCheckQuestions.slice(slides.length).forEach((q) => contentBlocks.push({ type: "sense_check", question: q }));
    }
  }

  const renderSlideCard = (slide: Slide) => (
    <Card key={`slide-${slide.title}`} className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/50 dark:to-slate-800/50">
      <CardContent className="p-8 md:p-10">
        <h2 className="text-2xl font-bold mb-6 text-foreground">{slide.title}</h2>
        <div className="flex flex-col md:flex-row gap-8 items-stretch">
          <div className="flex-1 space-y-4 min-w-0">
            {slide.paragraph ? (
              <p className="text-foreground/90 leading-relaxed">{slide.paragraph}</p>
            ) : (
              (slide.bullets || []).map((bullet, i) => (
                <div key={i} className="flex gap-3">
                  <span className="text-primary font-bold mt-0.5">▸</span>
                  <span className="text-foreground/90 leading-relaxed">{bullet}</span>
                </div>
              ))
            )}
          </div>
          <div className="md:w-2/5 flex-shrink-0 aspect-video md:aspect-square md:min-h-[280px] rounded-xl overflow-hidden shadow-md bg-muted/50 flex items-center justify-center">
            {slide.imageUrl ? (
              <img src={slide.imageUrl} alt={slide.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                <ImageIcon className="h-16 w-16 text-primary/30" />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <article className="w-full max-w-3xl mx-auto px-4 pb-24">
      {/* Prose typography - optimized for reading */}
      <style>{`
        .learner-prose h1 { font-size: 2rem; font-weight: 700; margin-top: 2.5rem; margin-bottom: 1rem; line-height: 1.3; }
        .learner-prose h2 { font-size: 1.5rem; font-weight: 600; margin-top: 2rem; margin-bottom: 0.75rem; padding-bottom: 0.25rem; border-bottom: 1px solid hsl(var(--border)); }
        .learner-prose h3 { font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.5rem; }
        .learner-prose p { margin-bottom: 1rem; line-height: 1.75; color: hsl(var(--foreground) / 0.9); }
        .learner-prose ul, .learner-prose ol { margin: 1rem 0; padding-left: 1.5rem; }
        .learner-prose li { margin-bottom: 0.5rem; line-height: 1.65; }
        .learner-prose img { border-radius: 0.75rem; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); margin: 1.5rem 0; max-width: 100%; height: auto; }
        .learner-prose blockquote { border-left: 4px solid hsl(var(--primary)); padding-left: 1rem; margin: 1.5rem 0; color: hsl(var(--muted-foreground)); font-style: italic; }
        .learner-prose hr { margin: 2rem 0; border-color: hsl(var(--border)); }
        .learner-prose table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; font-size: 0.9375rem; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1); }
        .learner-prose thead { background: hsl(var(--muted)); }
        .learner-prose th { padding: 0.75rem 1rem; text-align: left; font-weight: 600; border-bottom: 2px solid hsl(var(--border)); }
        .learner-prose td { padding: 0.75rem 1rem; border-bottom: 1px solid hsl(var(--border)); vertical-align: top; }
        .learner-prose tr:last-child td { border-bottom: none; }
        .learner-prose tbody tr:hover { background: hsl(var(--muted) / 0.5); }
        .learner-prose mark { padding: 0 0.1em; border-radius: 2px; }
      `}</style>

      {/* Annotations: notes, highlight, comments */}
      {hasContent && (
        <LearnerAnnotations
          skillNodeId={skillNodeId}
          annotationsContainerRef={annotationsContainerRef}
          notesPanelContainerId={notesPanelContainerId}
          notesPanelOpen={notesPanelOpen}
          onNotesPanelOpenChange={onNotesPanelOpenChange}
        />
      )}

      {!hasContent && (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <p className="text-muted-foreground">No lesson content available yet.</p>
          </CardContent>
        </Card>
      )}

      {/* Interleaved content: text sections, slides, and sense checks sprinkled throughout */}
      {contentBlocks.length > 0 && !mindMapLesson && !simulationLesson && (
        <div ref={annotationsContainerRef} className="space-y-10">
          {contentBlocks.map((block, i) => {
            if (block.type === "text") {
              return (
                <div key={`text-${i}`} className="learner-prose prose prose-slate dark:prose-invert max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {block.content}
                  </ReactMarkdown>
                </div>
              );
            }
            if (block.type === "slide") {
              return <div key={`slide-${i}`}>{renderSlideCard(block.slide)}</div>;
            }
            if (block.type === "sense_check") {
              return (
                <SenseCheckBlock
                  key={`sc-${block.question.id}-${i}`}
                  question={block.question}
                  answer={senseCheckAnswers[block.question.id]}
                  feedback={senseCheckFeedback[block.question.id]}
                  loading={senseCheckLoading[block.question.id]}
                  onAnswerChange={(id, val) => setSenseCheckAnswers((p) => ({ ...p, [id]: val }))}
                  onSubmit={() => handleSenseCheckSubmit(block.question.id)}
                />
              );
            }
            return null;
          })}
        </div>
      )}

      {/* Mind map lesson (dedicated full-width rendering) */}
      {mindMapLesson && skillNodeId && (
        <MindMapLessonView
          lesson={mindMapLesson}
          skillNodeId={skillNodeId}
          onLessonUpdated={(updated) => setMindMapLesson(updated)}
        />
      )}

      {/* Embedded simulation lesson (dedicated full-width rendering) */}
      {simulationLesson && skillNodeId && (
        <SimulationLessonView
          config={simulationLesson}
          skillNodeId={skillNodeId}
          courseId={courseId}
          completingLesson={completingLesson}
          lessonMarkedComplete={lessonMarkedComplete}
          onComplete={handleCompleteLessonWithoutExam}
        />
      )}

      {/* Fallback: essay or slides only without interleaving (e.g. essay with no ## splits) */}
      {hasContent && !mindMapLesson && !simulationLesson && contentBlocks.length === 0 && (
        <div ref={annotationsContainerRef}>
          {lessonContent && (
            <div className="learner-prose prose prose-slate dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {lessonContent}
              </ReactMarkdown>
            </div>
          )}
          {slides.length > 0 && !lessonContent && (
            <div className="space-y-10">
              {slides.map((slide, idx) => (
                <div key={idx}>
                  {renderSlideCard(slide)}
                  {senseCheckQuestions[idx] && (
                    <SenseCheckBlock
                      key={`sc-${senseCheckQuestions[idx].id}`}
                      question={senseCheckQuestions[idx]}
                      answer={senseCheckAnswers[senseCheckQuestions[idx].id]}
                      feedback={senseCheckFeedback[senseCheckQuestions[idx].id]}
                      loading={senseCheckLoading[senseCheckQuestions[idx].id]}
                      onAnswerChange={(id, val) => setSenseCheckAnswers((p) => ({ ...p, [id]: val }))}
                      onSubmit={() => handleSenseCheckSubmit(senseCheckQuestions[idx].id)}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          {lessonContent && slides.length === 0 && senseCheckQuestions[0] && (
            <SenseCheckBlock
              question={senseCheckQuestions[0]}
              answer={senseCheckAnswers[senseCheckQuestions[0].id]}
              feedback={senseCheckFeedback[senseCheckQuestions[0].id]}
              loading={senseCheckLoading[senseCheckQuestions[0].id]}
              onAnswerChange={(id, val) => setSenseCheckAnswers((p) => ({ ...p, [id]: val }))}
              onSubmit={() => handleSenseCheckSubmit(senseCheckQuestions[0].id)}
            />
          )}
        </div>
      )}

      {/* Further Reading */}
      {hasContent && (
        <FurtherReading nodeId={skillNodeId} nodeName={nodeTitle} courseId={courseId} />
      )}

      {/* Instructor lesson exam — pass to complete the node and unlock the next */}
      {nodeDetail?.exam && (
        <Card className="mt-16 border-2 border-primary/20 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {isKeyNode && <Star className="h-5 w-5 text-amber-500" />}
              Lesson exam
            </CardTitle>
            <CardDescription>
              Pass this exam to complete the lesson and unlock the next one
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!takingExam && !examResult && (
              <div className="flex items-center justify-between flex-wrap gap-3">
                <Badge variant="outline">Passing: {nodeDetail.exam.passing_score}%</Badge>
                <Button onClick={handleStartExam}>Start exam</Button>
              </div>
            )}

            {takingExam && examData && (
              <div className="space-y-6">
                {finalExamQuestions.map((q, idx) => (
                  <div key={q.id} className="rounded-xl border bg-card p-5 space-y-3">
                    <h4 className="font-medium">Question {idx + 1}</h4>
                    <p className="text-foreground/90">{q.prompt}</p>
                    {(q.question_type === "mcq" || q.question_type === "true_false") && q.options && (
                      <RadioGroup
                        value={examAnswers[q.id] || ""}
                        onValueChange={(v) => setExamAnswers((p) => ({ ...p, [q.id]: v }))}
                      >
                        {q.options.map((opt) => (
                          <div key={opt} className="flex items-center space-x-2">
                            <RadioGroupItem value={opt} id={`exam-${q.id}-${opt}`} />
                            <Label htmlFor={`exam-${q.id}-${opt}`} className="cursor-pointer">{opt}</Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}
                    {(q.question_type === "short_answer" || q.question_type === "fill_in_blank" || q.question_type === "capstone_essay") && (
                      q.question_type === "fill_in_blank" ? (
                        <Input
                          placeholder="Your answer"
                          value={examAnswers[q.id] || ""}
                          onChange={(e) => setExamAnswers((p) => ({ ...p, [q.id]: e.target.value }))}
                          className="max-w-md"
                        />
                      ) : (
                        <Textarea
                          placeholder={q.question_type === "capstone_essay" ? "Write your response (apply concepts to the scenario)..." : "Type your answer..."}
                          value={examAnswers[q.id] || ""}
                          onChange={(e) => setExamAnswers((p) => ({ ...p, [q.id]: e.target.value }))}
                          className={q.question_type === "capstone_essay" ? "min-h-[200px] resize-y" : "min-h-[80px] resize-y"}
                          rows={q.question_type === "capstone_essay" ? 10 : 3}
                        />
                      )
                    )}
                  </div>
                ))}
                <div className="flex gap-3">
                  <Button onClick={handleSubmitExam} disabled={submitting} className="flex-1">
                    {submitting ? "Submitting..." : "Submit exam"}
                  </Button>
                  <Button variant="outline" onClick={() => setTakingExam(false)}>Cancel</Button>
                </div>
              </div>
            )}

            {examResult && (
              <div className="space-y-6">
                <div className={`rounded-xl p-5 space-y-3 ${examResult.passed ? "bg-green-500/10 border border-green-500/30" : "bg-red-500/10 border border-red-500/30"}`}>
                  <div className="flex items-center gap-2">
                    {examResult.passed ? <CheckCircle className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-red-500" />}
                    <span className="font-semibold">{examResult.passed ? "Passed!" : "Not quite"}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Overall score: {examResult.score}%. {examResult.passed ? "Lesson complete. Next lessons unlocked." : `You need ${nodeDetail.exam.passing_score}% overall to pass. Open-ended items can earn partial credit toward your total.`}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {examResult.passed ? (
                      <Button onClick={onProgressUpdate} className="w-full sm:w-auto">
                        Continue to next lesson
                      </Button>
                    ) : (
                      <Button onClick={handleStartExam} variant="outline">Try again</Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setExamResult(null); setTakingExam(false); }}>
                      Close report
                    </Button>
                  </div>
                </div>

                <div className="rounded-xl border bg-card p-4 space-y-3">
                  <h3 className="font-semibold text-base">Your report</h3>
                  <p className="text-sm text-muted-foreground">
                    Each row shows how much credit you earned on that question (0–100%). Open-ended and capstone items include an AI assessment.
                  </p>
                  <div className="space-y-2">
                    {(examResult.feedbackPerQuestion as ExamAttemptFeedback[] | undefined)?.map((f, idx) => {
                      const credit = examCreditPercent(f);
                      const status = creditStatusLabel(credit);
                      const typeLabel = f.questionType ?? "question";
                      return (
                        <Collapsible key={f.questionId} defaultOpen={credit < 100} className="group">
                          <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-left text-sm hover:bg-muted/60">
                            <span className="font-medium">
                              Q{idx + 1} · <span className="text-muted-foreground font-normal capitalize">{typeLabel.replace(/_/g, " ")}</span>
                            </span>
                            <span className="flex items-center gap-2 shrink-0">
                              <Badge variant={status.variant}>{credit}%</Badge>
                              <Badge variant="outline">{status.label}</Badge>
                              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                            </span>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="space-y-3 border border-t-0 rounded-b-lg px-3 py-3 text-sm">
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Prompt</p>
                              <p className="text-foreground/90 mt-1">{f.prompt}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Your answer</p>
                              <p className="text-foreground/90 mt-1 whitespace-pre-wrap">{f.userAnswer?.trim() || "—"}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Model / expected answer</p>
                              <p className="text-foreground/90 mt-1 whitespace-pre-wrap">{String(f.correctAnswer ?? "").trim() || "—"}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Badge variant="outline">{f.pointsEarned} / {f.pointsPossible} pts</Badge>
                              {typeof f.aiScorePercent === "number" && (
                                <Badge variant="secondary">AI concept score: {f.aiScorePercent}%</Badge>
                              )}
                            </div>
                            {f.explanation?.trim() && (
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Instructor note</p>
                                <p className="text-foreground/90 mt-1">{f.explanation}</p>
                              </div>
                            )}
                            {f.aiRationale?.trim() && (
                              <div className="rounded-md bg-amber-500/10 border border-amber-500/20 p-3">
                                <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 uppercase tracking-wide">AI assessment</p>
                                <p className="text-sm mt-1 text-foreground/90">{f.aiRationale}</p>
                              </div>
                            )}
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mastery check — replaces "Complete & continue" when no instructor exam */}
      {!loading && !nodeDetail?.exam && courseId && (
        <Card className="mt-16 border-2 border-primary/20 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Mastery check
            </CardTitle>
            <CardDescription>
              Answer these questions to complete the lesson and unlock the next one. You need 60% to pass.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {lessonMarkedComplete ? (
              <p className="text-sm text-muted-foreground">Lesson complete. Use the back button to return to your learning path.</p>

            ) : masteryResult?.passed ? (
              <div className="space-y-3">
                <div className="rounded-lg bg-green-500/10 border border-green-500/30 p-4 flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    Passed! {masteryResult.score}/{masteryResult.total} correct
                  </p>
                </div>
                <Button onClick={handleCompleteLessonWithoutExam} disabled={completingLesson} className="w-full sm:w-auto">
                  {completingLesson ? "Completing…" : "Continue to next lesson"}
                </Button>
              </div>

            ) : masteryResult && !masteryResult.passed ? (
              <div className="space-y-4">
                <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-4 flex items-center gap-3">
                  <XCircle className="h-5 w-5 text-red-500 shrink-0" />
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    {masteryResult.score}/{masteryResult.total} correct — need {Math.ceil(masteryResult.total * 0.6)} to pass. Review the explanations below and try again.
                  </p>
                </div>
                <div className="space-y-3">
                  {masteryQuestions!.map((q, i) => {
                    const fb = masteryResult.feedback.find((f) => f.id === q.id);
                    return (
                      <div key={q.id} className={`rounded-lg border p-3 text-sm ${fb?.userCorrect ? "border-green-500/30 bg-green-500/5" : "border-red-500/30 bg-red-500/5"}`}>
                        <p className="font-medium mb-1">Q{i + 1}. {q.prompt}</p>
                        {q.type === "mcq" && q.options && (
                          <p className="text-muted-foreground text-xs mb-1">
                            Correct: <span className="font-medium text-foreground">{q.options[q.correctIndex!]}</span>
                          </p>
                        )}
                        {q.type === "true_false" && (
                          <p className="text-muted-foreground text-xs mb-1">
                            Correct: <span className="font-medium text-foreground">{q.correct ? "True" : "False"}</span>
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">{q.explanation}</p>
                      </div>
                    );
                  })}
                </div>
                <Button variant="outline" className="gap-2" onClick={generateMasteryCheck}>
                  <RefreshCw className="h-4 w-4" />
                  Try again with new questions
                </Button>
              </div>

            ) : masteryQuestions ? (
              <div className="space-y-6">
                {masteryQuestions.map((q, i) => (
                  <div key={q.id} className="space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="shrink-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-0.5">
                        Q{i + 1} · {q.difficulty}
                      </span>
                    </div>
                    <p className="font-medium text-sm leading-snug">{q.prompt}</p>
                    {q.type === "mcq" && q.options && (
                      <RadioGroup
                        value={masteryAnswers[q.id] ?? ""}
                        onValueChange={(v) => setMasteryAnswers((p) => ({ ...p, [q.id]: v }))}
                        className="space-y-1.5"
                      >
                        {q.options.map((opt, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <RadioGroupItem value={String(idx)} id={`mq-${q.id}-${idx}`} />
                            <Label htmlFor={`mq-${q.id}-${idx}`} className="cursor-pointer text-sm">{opt}</Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}
                    {q.type === "true_false" && (
                      <RadioGroup
                        value={masteryAnswers[q.id] ?? ""}
                        onValueChange={(v) => setMasteryAnswers((p) => ({ ...p, [q.id]: v }))}
                        className="flex gap-4"
                      >
                        {[{ label: "True", value: "true" }, { label: "False", value: "false" }].map(({ label, value }) => (
                          <div key={value} className="flex items-center space-x-2">
                            <RadioGroupItem value={value} id={`mq-${q.id}-${value}`} />
                            <Label htmlFor={`mq-${q.id}-${value}`} className="cursor-pointer text-sm">{label}</Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}
                  </div>
                ))}
                <Button onClick={submitMasteryCheck} className="w-full sm:w-auto">
                  Submit answers
                </Button>
              </div>

            ) : (
              <Button onClick={generateMasteryCheck} disabled={masteryLoading} className="gap-2 w-full sm:w-auto">
                {masteryLoading
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating questions…</>
                  : <><Trophy className="h-4 w-4" /> Take mastery check</>
                }
              </Button>
            )}
          </CardContent>
        </Card>
      )}
      {/* Floating AI tutor chat — visible while reading */}
      {courseId && (
        <LessonTutorChat
          courseId={courseId}
          nodeId={skillNodeId}
          nodeTitle={nodeTitle}
          log={log}
        />
      )}
    </article>
  );
}

function SenseCheckBlock({
  question,
  answer,
  feedback,
  loading,
  onAnswerChange,
  onSubmit,
}: {
  question: ExamQuestion;
  answer?: string;
  feedback?: { correct: boolean; show: boolean; explanation?: string };
  loading?: boolean;
  onAnswerChange: (id: string, v: string) => void;
  onSubmit: () => void;
}) {
  return (
    <Card className="mt-8 border-amber-500/30 bg-amber-50/30 dark:bg-amber-950/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-amber-600" />
          Sense check
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="font-medium text-foreground">{question.prompt}</p>
        {(question.question_type === "mcq" || question.question_type === "true_false") && question.options && (
          <RadioGroup value={answer || ""} onValueChange={(v) => onAnswerChange(question.id, v)}>
            {question.options.map((opt) => (
              <div key={opt} className="flex items-center space-x-2">
                <RadioGroupItem value={opt} id={`sc-${question.id}-${opt}`} />
                <Label htmlFor={`sc-${question.id}-${opt}`} className="cursor-pointer text-sm">{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        )}
        {(question.question_type === "short_answer" || question.question_type === "fill_in_blank") && (
          question.question_type === "short_answer" ? (
            <Textarea
              value={answer || ""}
              onChange={(e) => onAnswerChange(question.id, e.target.value)}
              placeholder="Type your answer..."
              rows={2}
              className="resize-none"
            />
          ) : (
            <Input
              value={answer || ""}
              onChange={(e) => onAnswerChange(question.id, e.target.value)}
              placeholder="Your answer"
            />
          )
        )}
        {!feedback?.show && <Button size="sm" onClick={onSubmit} disabled={!answer || loading}>{loading ? "Checking..." : "Check answer"}</Button>}
        {feedback?.show && (
          <div className={`text-sm rounded-lg p-3 ${feedback.correct ? "bg-green-500/10 text-green-800 dark:text-green-200" : "bg-amber-500/10 text-amber-800 dark:text-amber-200"}`}>
            {feedback.correct ? <><CheckCircle className="h-4 w-4 inline mr-1" /> Correct!</> : <><XCircle className="h-4 w-4 inline mr-1" /> {feedback.explanation || "Review the section above."}</>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
